import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { LEGAL, LAST_UPDATED } from "../content/legal";

const fill = (t, vars) => t.replace(/\{op\}/g, vars.op).replace(/\{email\}/g, vars.email);

const Block = ({ block, vars }) =>
  Array.isArray(block) ? (
    <ul className="list-disc space-y-1.5 pl-5 marker:text-[var(--saffron-deep)]">
      {block.map((li) => <li key={li}>{fill(li, vars)}</li>)}
    </ul>
  ) : (
    <p>{fill(block, vars)}</p>
  );

const OTHER = { privacy: ["terms", "Terms of Use"], terms: ["disclaimer", "Disclaimer"], disclaimer: ["privacy", "Privacy Policy"] };

export default function Legal({ page }) {
  const p = LEGAL[page];
  const { data: config } = useQuery({ queryKey: ["public-config"], queryFn: () => api.get("/public/config").then((r) => r.data) });
  const vars = { op: config?.operator_name || "PropertiesforNRI.com", email: config?.contact_email || "our contact email" };
  const [nextPath, nextLabel] = OTHER[page];

  return (
    <div className="min-h-screen bg-white" data-testid={`legal-page-${page}`}>
      <Header />
      <article className="mx-auto max-w-2xl px-5 py-10">
        <Link to="/" className="mb-6 inline-flex min-h-[44px] items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900" data-testid="legal-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <h1 className="font-display text-4xl text-[#1f2328] sm:text-5xl" data-testid="legal-title">{p.title}</h1>
        <p className="mt-2 text-xs uppercase tracking-[0.14em] text-neutral-500" data-testid="legal-last-updated">Last updated: {LAST_UPDATED}</p>
        <p className="mt-6 text-[15px] leading-relaxed text-neutral-700">{fill(p.intro, vars)}</p>
        <nav className="mt-6 rounded-2xl bg-[var(--ivory)] p-4 text-sm" data-testid="legal-toc">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">On this page</p>
          <ol className="grid gap-1 sm:grid-cols-2">
            {p.sections.map(([h], i) => (
              <li key={h}><a href={`#s${i}`} className="text-neutral-700 hover:text-[#138808]">{h}</a></li>
            ))}
          </ol>
        </nav>
        <div className="mt-8 space-y-8">
          {p.sections.map(([heading, ...blocks], i) => (
            <section key={heading} id={`s${i}`} className="scroll-mt-20" data-testid={`legal-section-${i}`}>
              <h2 className="font-display text-xl text-[#1f2328]">{heading}</h2>
              <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-neutral-700">
                {blocks.map((b, j) => <Block key={j} block={b} vars={vars} />)}
              </div>
            </section>
          ))}
        </div>
        <Link to={`/${nextPath}`} className="mt-10 inline-flex min-h-[44px] items-center text-sm font-semibold text-[#138808]" data-testid="legal-next-link">
          Read our {nextLabel} →
        </Link>
      </article>
      <Footer config={config} />
    </div>
  );
}
